import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Warning,
  Person,
  PersonOff,
  Business,
  LocationOn
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';

/**
 * Confirmation dialog for employee deactivation with cascade options
 * @param {Object} props - Component props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Handler to close dialog
 * @param {Function} props.onConfirm - Handler for confirmation with cascade options
 * @param {Object} props.employee - Employee record being deactivated
 * @param {boolean} props.loading - Loading state during deactivation
 * @returns {React.Component} Deactivation confirmation dialog
 */
function DeactivateDialog({ 
  open, 
  onClose, 
  onConfirm, 
  employee, 
  loading = false 
}) {
  const [deactivateUser, setDeactivateUser] = useState(false);
  const hasUser = !!employee?.userId;
  
  const handleConfirm = () => {
    onConfirm({
      employeeId: employee.id,
      deactivateUser: hasUser && deactivateUser
    });
  };

  if (!employee) return null;

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="warning" />
          <Typography variant="h6">
            Deactivate Employee
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are about to deactivate {employee.firstName} {employee.lastName}.
        </Alert>
        
        <Typography variant="body2" paragraph>
          This action will have the following effects:
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <Person fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Employee status"
              secondary="Employee will be marked as inactive and terminated"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <LocationOn fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Location assignments"
              secondary="All location assignments will be ended"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <Business fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Organization unit assignments"
              secondary="All organization unit assignments will be ended"
            />
          </ListItem>
        </List>
        
        {hasUser && (
          <>
            <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
              This employee is a RadixDash user with an active account.
            </Alert>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={deactivateUser}
                  onChange={(e) => setDeactivateUser(e.target.checked)}
                  disabled={loading}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Also deactivate their User account
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This will revoke their RadixDash access immediately
                  </Typography>
                </Box>
              }
            />
          </>
        )}
        
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            This action cannot be undone automatically.
          </Typography>
          <Typography variant="body2">
            You can reactivate the employee later, but assignments will need to be manually recreated.
          </Typography>
        </Alert>
      </DialogContent>
      
      <DialogActions>
        <AdminButton
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </AdminButton>
        <AdminButton
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? null : <PersonOff />}
        >
          {loading ? 'Deactivating...' : 'Deactivate Employee'}
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

DeactivateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    userId: PropTypes.string
  }),
  loading: PropTypes.bool
};

export default DeactivateDialog;